import {getViewSettingsSafely, ViewIdentifiable} from "../../models/common/ViewSettings";
import {ApiReturnType} from "../../models/common/ApiReturnType";
import {Employer, EmployersId} from "../../models/users/Employee";
import {
    FETCH_USER_VIEW_SETTINGS_SUCCESS,
    HANDLE_SEARCH_TRIGGERED, sendUserToPage,
    VIEW_SORT_CHANGE,
    ViewActionTypes
} from "../common/UserViewSettingsActions";
import {AppState} from "../../reducers/RootReducer";
import {ofType, StateObservable} from "redux-observable";
import {getUrlParamsFromViewSettings, RouteConfig, RouteMethods} from "../../config/RouteConfig";
import {catchError, filter, map, mergeMap, switchMap} from "rxjs/operators";
import { Observable, of } from 'rxjs';
import {ajax} from 'rxjs/ajax';
import {APP_CONFIG} from "../../config/GeneralConfig";
import {
    eventNotificationError,
    eventNotificationShow,
    eventNotificationSuccess
} from "../common/EventNotificationActions";
import {EVENT_NOTIFICATION_TYPE} from "../../helpers/EventNotificationType";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {Identifier} from "../../models/common/Identifier";
import {Nullable} from "../../models/common/Nullable";
import {EmployerModule} from "../../models/employer/EmployerModule";



/**
 * base API path to interact with project roles
 */
function apiPath( id: Nullable<number> = null ): string {
    return APP_CONFIG.baseWeb + "employers/"+ ( id !== null ? id : '');
}


/**
 * action types
 */
export type EmployersActionTypes =  FetchEmployersActionTypes
    | CreateEmployerActionTypes
    | UpdateEmployerActionTypes
    | DeleteEmployerActionTypes
    | RestoreEmployerActionTypes
    | FetchEmployerByIdActionTypes

/**
 * fetch employers
 **/
export interface FetchEmployersAction extends ViewIdentifiable {
    type: typeof FETCH_EMPLOYERS
}

export interface FetchEmployersSuccessAction extends ViewIdentifiable {
    type: typeof FETCH_EMPLOYERS_SUCCESS
    payload: ApiReturnType< Employer[] >
}

export interface FetchEmployersErrorAction extends ViewIdentifiable {
    type: typeof FETCH_EMPLOYERS_ERROR
    error: Error
}

export type FetchEmployersActionTypes = FetchEmployersAction | FetchEmployersSuccessAction | FetchEmployersErrorAction

export const FETCH_EMPLOYERS = "FETCH_EMPLOYERS";
export const FETCH_EMPLOYERS_SUCCESS = "FETCH_EMPLOYERS_SUCCESS";
export const FETCH_EMPLOYERS_ERROR = "FETCH_EMPLOYERS_ERROR";

export const fetchEmployers = ( viewIdentifier: string ): FetchEmployersAction => ({ type: FETCH_EMPLOYERS, viewIdentifier });

export const fetchEmployersSuccess = ( viewIdentifier: string, payload: ApiReturnType< Employer[] > ): FetchEmployersSuccessAction =>
    ({ type: FETCH_EMPLOYERS_SUCCESS, viewIdentifier, payload });

export const fetchEmployersError = ( viewIdentifier: string, error: Error ): FetchEmployersErrorAction =>
    ({ type: FETCH_EMPLOYERS_ERROR, viewIdentifier, error });

export const fetchEmployersEpic = (
    action$: Observable< FetchEmployersAction | ViewActionTypes >,
    store$: StateObservable< AppState >
) =>
    action$.pipe(
        ofType( VIEW_SORT_CHANGE, FETCH_EMPLOYERS, HANDLE_SEARCH_TRIGGERED, FETCH_USER_VIEW_SETTINGS_SUCCESS ),
        filter( action => action.viewIdentifier === RouteConfig.employers.viewIdentifier ),
        mergeMap( action => {
            const userViewSettings = getViewSettingsSafely( action.viewIdentifier, store$ );
            const path = `${ APP_CONFIG.baseWeb }employers/${ getUrlParamsFromViewSettings( userViewSettings )}`;

            return ajax( APP_CONFIG.getRequestObject(path) )
                .pipe(
                    map( response => fetchEmployersSuccess(
                        action.viewIdentifier,
                        response.response as ApiReturnType< Employer[] >
                    ) ),
                    catchError( error => of(
                        fetchEmployersError( action.viewIdentifier, error ),
                        eventNotificationShow(
                            EVENT_NOTIFICATION_TYPE.customAjaxError,
                            "Arbeitgeber konnten nicht geladen!"
                        )
                    ) )
                )
        } )
    );




/**
 * FETCH SINGLE EMPLOYER
 */
export interface FetchEmployerByIdAction extends ReduxAction< typeof FETCH_EMPLOYER_BY_ID> {
   employerId: Identifier<number>
}
export interface FetchEmployerByIdSuccessAction extends ReduxAction< typeof FETCH_EMPLOYER_BY_ID_SUCCESS> {
    employer: Employer
}
export interface FetchEmployerByIdErrorAction extends ErrorAction< typeof FETCH_EMPLOYER_BY_ID_ERROR> {
    employerId: Identifier<number>
}
//union of action to fetch by id
export type FetchEmployerByIdActionTypes = FetchEmployerByIdAction
    | FetchEmployerByIdSuccessAction
    | FetchEmployerByIdErrorAction

// REDUX ACTION TYPE CONSTANTS
export const FETCH_EMPLOYER_BY_ID = "FETCH_EMPLOYER_BY_ID";
export const FETCH_EMPLOYER_BY_ID_SUCCESS = "FETCH_EMPLOYER_BY_ID_SUCCESS";
export const FETCH_EMPLOYER_BY_ID_ERROR = "FETCH_EMPLOYER_BY_ID_ERROR";

//Redux action accessors function
export function fetchEmployerById( employerId: Identifier<number> ): FetchEmployerByIdAction {
    return { type: FETCH_EMPLOYER_BY_ID, employerId };
}
export function fetchEmployerByIdSuccess( employer: Employer ): FetchEmployerByIdSuccessAction {
    return { type: FETCH_EMPLOYER_BY_ID_SUCCESS, employer };
}
export function fetchEmployerByIdError( error: any, employerId: Identifier<number> ): FetchEmployerByIdErrorAction {
    return { type: FETCH_EMPLOYER_BY_ID_ERROR, error, employerId };
}

//redux observable epic as bridge to rxjs
export function fetchEmployerByIdEpic(
    action$: Observable< FetchEmployerByIdAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_EMPLOYER_BY_ID ),
        switchMap( action => {

                return ajax(
                    APP_CONFIG.getRequestObject( apiPath( action.employerId.rawValue ) )
                ).pipe(
                    map( response => fetchEmployerByIdSuccess( response.response as Employer ) ),
                    catchError( error => of(
                        fetchEmployerByIdError( error, action.employerId ),
                        eventNotificationError( error, store$, EmployerModule.identifier, "fetchEmployerById" )
                    ) )
                )
            }
        )
    );
}


/**
 * CREATE EMPLOYER
 */
export interface CreateEmployerAction extends ReduxAction<typeof CREATE_EMPLOYER> {
    employer: Employer
}
export interface CreateEmployerSuccessAction extends ReduxAction<typeof CREATE_EMPLOYER_SUCCESS> {

}
export interface CreateEmployerErrorAction extends ErrorAction<typeof CREATE_EMPLOYER_ERROR> {

}
// typescript union type of create actions
export type CreateEmployerActionTypes = CreateEmployerAction
    | CreateEmployerSuccessAction
    | CreateEmployerErrorAction

//redux action constants
export const CREATE_EMPLOYER = "CREATE_EMPLOYER";
export const CREATE_EMPLOYER_SUCCESS = "CREATE_EMPLOYER_SUCCESS";
export const CREATE_EMPLOYER_ERROR = "CREATE_EMPLOYER_ERROR";

//redux action accessors functions
export function createEmployer( employer: Employer ): CreateEmployerAction {
    return { type: CREATE_EMPLOYER,  employer };
}
export function createEmployerSuccess(): CreateEmployerSuccessAction {
    return { type: CREATE_EMPLOYER_SUCCESS  };
}
export function createEmployerError( error: any ): CreateEmployerErrorAction {
    return { type: CREATE_EMPLOYER_ERROR, error };
}

//redux observable rxjs bridge
export function createEmployerEpic(
    action$: Observable< CreateEmployerAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( CREATE_EMPLOYER ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath(),
                    RouteMethods.post,
                    action.employer
                )
            ).pipe(
                switchMap( response => of(
                    createEmployerSuccess( ),
                    fetchEmployers( EmployerModule.identifier ),
                    eventNotificationSuccess(),
                    sendUserToPage(
                        RouteConfig.employers.routes.main.path,
                        EmployerModule.identifier
                    )
                ) ),
                catchError( error => of(
                    createEmployerError( error ),
                    eventNotificationError( error, store$, EmployerModule.identifier, "createEmployer" )
                ))
            )
        )
    )
}

/**
 * UPDATE EMPLOYER
 */
export interface UpdateEmployerAction extends ReduxAction<typeof UPDATE_EMPLOYER> {
    employer: Employer
}
export interface UpdateEmployerSuccessAction extends ReduxAction<typeof UPDATE_EMPLOYER_SUCCESS> {

}
export interface UpdateEmployerErrorAction extends ErrorAction<typeof UPDATE_EMPLOYER_ERROR> {

}
// typescript union type of update actions
export type UpdateEmployerActionTypes = UpdateEmployerAction
    | UpdateEmployerSuccessAction
    | UpdateEmployerErrorAction

//redux action constants
export const UPDATE_EMPLOYER = "UPDATE_EMPLOYER";
export const UPDATE_EMPLOYER_SUCCESS = "UPDATE_EMPLOYER_SUCCESS";
export const UPDATE_EMPLOYER_ERROR = "UPDATE_EMPLOYER_ERROR";

//redux action accessors functions
export function updateEmployer( employer: Employer ): UpdateEmployerAction {
    return { type: UPDATE_EMPLOYER, employer };
}
export function updateEmployerSuccess(): UpdateEmployerSuccessAction {
    return { type: UPDATE_EMPLOYER_SUCCESS };
}
export function updateEmployerError( error: any ): UpdateEmployerErrorAction {
    return { type: UPDATE_EMPLOYER_ERROR, error };
}

//redux observable rxjs bridge
export function updateEmployerEpic(
    action$: Observable< UpdateEmployerAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( UPDATE_EMPLOYER ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath( action.employer.employersId.rawValue ),
                    RouteMethods.put,
                    action.employer
                )
            ).pipe(
                switchMap( response => of(
                    updateEmployerSuccess(),
                    fetchEmployers( EmployerModule.identifier ),
                    eventNotificationSuccess(),
                    sendUserToPage(
                        RouteConfig.employers.routes.main.path,
                        EmployerModule.identifier
                    )
                ) ),
                catchError( error => of(
                    updateEmployerError( error ),
                    eventNotificationError( error, store$, EmployerModule.identifier, "updateEmployer" )
                ))
            )
        )
    )
}

/**
 * DELETE EMPLOYER
 */
export interface DeleteEmployerAction extends ReduxAction<typeof DELETE_EMPLOYER> {
    employerId: Identifier<number>
}
export interface DeleteEmployerSuccessAction extends ReduxAction<typeof DELETE_EMPLOYER_SUCCESS> {

}
export interface DeleteEmployerErrorAction extends ErrorAction<typeof DELETE_EMPLOYER_ERROR> {

}
// typescript union type of delete actions
export type DeleteEmployerActionTypes = DeleteEmployerAction
    | DeleteEmployerSuccessAction
    | DeleteEmployerErrorAction

//redux action constants
export const DELETE_EMPLOYER = "DELETE_EMPLOYER";
export const DELETE_EMPLOYER_SUCCESS = "DELETE_EMPLOYER_SUCCESS";
export const DELETE_EMPLOYER_ERROR = "DELETE_EMPLOYER_ERROR";

//redux action accessors functions
export function deleteEmployer( employerId: Identifier<number>  ): DeleteEmployerAction {
    return { type: DELETE_EMPLOYER, employerId };
}
export function deleteEmployerSuccess( ): DeleteEmployerSuccessAction {
    return { type: DELETE_EMPLOYER_SUCCESS };
}
export function deleteEmployerError( error: any ): DeleteEmployerErrorAction {
    return { type: DELETE_EMPLOYER_ERROR, error };
}

//redux observable rxjs bridge
export function deleteEmployerEpic(
    action$: Observable< DeleteEmployerAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( DELETE_EMPLOYER ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath( action.employerId.rawValue ),
                    RouteMethods.delete
                )
            ).pipe(
                switchMap( _ => of(
                    deleteEmployerSuccess(  ),
                    eventNotificationSuccess(),
                    fetchEmployerById( action.employerId )
                ) ),
                catchError( error => of(
                    deleteEmployerError( error ),
                    eventNotificationError( error, store$, EmployerModule.identifier, "deleteEmployer" )
                ))
            )
        )
    )
}

/**
 * RESTORE EMPLOYER
 */
export interface RestoreEmployerAction extends ReduxAction<typeof RESTORE_EMPLOYER> {
    employerId: Identifier<number>
}
export interface RestoreEmployerSuccessAction extends ReduxAction<typeof RESTORE_EMPLOYER_SUCCESS> {

}
export interface RestoreEmployerErrorAction extends ErrorAction<typeof RESTORE_EMPLOYER_ERROR> {

}
// typescript union type of restore actions
export type RestoreEmployerActionTypes = RestoreEmployerAction
    | RestoreEmployerSuccessAction
    | RestoreEmployerErrorAction

//redux action constants
export const RESTORE_EMPLOYER = "RESTORE_EMPLOYER";
export const RESTORE_EMPLOYER_SUCCESS = "RESTORE_EMPLOYER_SUCCESS";
export const RESTORE_EMPLOYER_ERROR = "RESTORE_EMPLOYER_ERROR";

//redux action accessors functions
export function restoreEmployer( employerId: Identifier<number>  ): RestoreEmployerAction {
    return { type: RESTORE_EMPLOYER, employerId };
}
export function restoreEmployerSuccess( ): RestoreEmployerSuccessAction {
    return { type: RESTORE_EMPLOYER_SUCCESS };
}
export function restoreEmployerError( error: any ): RestoreEmployerErrorAction {
    return { type: RESTORE_EMPLOYER_ERROR, error };
}

//redux observable rxjs bridge
export function restoreEmployerEpic(
    action$: Observable< RestoreEmployerAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( RESTORE_EMPLOYER ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath( action.employerId.rawValue ),
                    RouteMethods.patch
                )
            ).pipe(
                switchMap( _ => of(
                    restoreEmployerSuccess(),
                    eventNotificationSuccess(),
                    fetchEmployerById( action.employerId )
                ) ),
                catchError( error => of(
                    restoreEmployerError( error ),
                    eventNotificationError( error, store$, EmployerModule.identifier, "restoreEmployer" )
                ))
            )
        )
    )
}
